import React from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import InputMask from 'react-input-mask';
import { jwtDecode } from 'jwt-decode';

import { useTimeModal } from '../TimeModal/TimeModalContext';
import { formatDateTime, normalizeDate } from '../../Utils/GlobalUtils';

import FileUploadField from '../FileUploadField';
import ViewFileById from '../ViewFileById';
import FilesMiniTable from '../FilesMiniTable';
import AvatarEditor from '../AvatarEditor';

import Loading from '../Loading';
import LoadingBlock from '../LoadingBlock'
import WarningPanel from '../WarningPanel';

import styles from '../../styles/Account.module.css';

const MainInfo = ({ userId }) => {
  const { openTimeModal } = useTimeModal();
  const navigate = useNavigate();
  const location = useLocation();

  const [isFormChanged, setIsFormChanged] = React.useState(false);
  const [formInfo, setFormInfo] = React.useState({
    id: '',
    lastname: '',
    firstname: '',
    surname: '',
    date_of_birth: '',
    gender: 'Муж',
    place_of_birth: '',
    registry_am_number: '',
    TIN: '',
    SNILS: '',
    registration_address: '',
    postal_address: '',
    email: '',
    additional_email: '',
    phone_number: '',
    additional_phone_number: '',
    role: '',
    files: []
  });
  const [isSameAddress, setIsSameAddress] = React.useState(false);

  const [isAdmin, setIsAdmin] = React.useState(false);

  const [reload, setReload] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [warning, setWarning] = React.useState('');
  const [status, setStatus] = React.useState(<a>Сохраните изменения</a>);

  const handleLogout = () => {
      localStorage.removeItem('token');
      window.location.href = '/';
  }

  const [importantAccountData, setImportantAccountData] = React.useState([]);

  React.useEffect(() => {
      const token = localStorage.getItem('token');
      if (!token) handleLogout();

      axios.get('/api/get-important-account-data', {
          headers: {
              Authorization: `Bearer ${token}`,
          }
      }).then((response) => {
          setImportantAccountData(response.data);
      }).catch((error) => {
          console.error('Ошибка при получении данных пользователя:', error);
      }).finally(() => {
          setLoading(false);
      });
  }, []);

  React.useEffect(() => {
      const token = localStorage.getItem('token');
      if (!token) handleLogout();

      const decodedToken = jwtDecode(token);

      if (decodedToken.role==='АппаратСРО') setIsAdmin(true);

      axios.get('/api/get-account-info', {
          headers: {
              Authorization: `Bearer ${token}`,
          },
          params: { userId }
      }).then((response) => {
          setFormInfo(response.data);
          setIsSameAddress(response.data.postal_address === response.data.registration_address);
          setIsFormChanged(false);
      }).catch((error) => {
          console.error(error);
          setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
      }).finally(() => {
        setReload(false);
        setLoading(false);
      });
  }, [reload, userId]);

  const [templateConsentFileId, setTemplateConsentFileId] = React.useState(null);
  React.useEffect(() => {
      setLoading(true);

      axios.get('/api/get-template-consent-file-id')
      .then((response) => {
          setTemplateConsentFileId(response.data.file_id);
      }).catch((error) => {
          console.error(error);
      }).finally(() => {
          setLoading(false);
      })
  }, []);

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setFormInfo((prev) => ({ ...prev, [field]: value }));
    setIsFormChanged(true);

    setErrorsForm((prevErrors) => {
      if (!prevErrors[field]) return prevErrors;
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[field];
      return updatedErrors;
    });
  };

  React.useEffect(() => {
    setStatus(<a>Сохраните изменения</a>);
  }, [isFormChanged]);


  const handleSameAddressToggle = (e) => {
    const checked = e.target.checked;
    setIsSameAddress(checked);

    setIsFormChanged(true);
  };

  React.useEffect(() => {
    if (isSameAddress) {
      setFormInfo((prev) => ({
        ...prev,
        postal_address: prev.registration_address,
      }));
    }
  }, [formInfo.registration_address, isSameAddress]);

  const [errorsForm, setErrorsForm] = React.useState({});
  const validateForm = () => {
    const newErrors = {};

    if (!formInfo.lastname || formInfo.lastname.length < 2 || formInfo.lastname.length > 35) {
      newErrors.lastname = 'Значение не может быть меньше 2 и более 35 символов';
    }
    if (!formInfo.firstname || formInfo.firstname.length < 2 || formInfo.firstname.length > 20) {
      newErrors.firstname = 'Значение не может быть меньше 2 и более 20 символов';
    }
    if (!formInfo.surname || formInfo.surname.length < 2 || formInfo.surname.length > 20) {
      newErrors.surname = 'Значение не может быть меньше 2 и более 20 символов';
    }

    if (formInfo.registration_address?.length > 150) {
      newErrors.registration_address	 = 'Поле не может быть пустым и не должно превышать 150 символов';
    }
    if (formInfo.postal_address?.length > 150) {
      newErrors.postal_address = 'Поле не может быть пустым и не должно превышать 150 символов';
    }

    if (formInfo.TIN) {
      if (!/^\d{12}$/.test(formInfo.TIN)) {
        newErrors.TIN = (newErrors.TIN || '') + 'ИНН должен содержать 12 цифр';
      }
      // if (!files.TIN) {
      //   newErrors.TIN = (newErrors.TIN || '') + (newErrors.TIN ? '. ' : '') + 'При указании ИНН необходимо прикрепить скан документа';
      // }
    }
    if (files.TIN && !formInfo.TIN) {
      newErrors.TIN = 'Укажите ИНН';
    }

    if (!formInfo.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formInfo.email)) {
      newErrors.email = 'Введите корректный адрес электронной почты';
    }
    if (formInfo.additional_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formInfo.additional_email)) {
      newErrors.additional_email = 'Введите корректный адрес электронной почты';
    }
    if (formInfo.phone_number && !/^7\d{10}$/.test(formInfo.phone_number.replace(/\D/g, ''))) {
      newErrors.phone_number = 'Введите корректный номер телефона';
    }
    if (formInfo.additional_phone_number && !/^7\d{10}$/.test(formInfo.additional_phone_number.replace(/\D/g, ''))) {
      newErrors.additional_phone_number = 'Введите корректный номер телефона';
    }

    setErrorsForm(newErrors);
    if (Object.keys(newErrors).length > 0) {
        setStatus(<a className='mainLabelError'>Данные не сохранены. Проверьте корректность введенных полей</a>);
        return false;
    } else {
        return true;
    }
  };

  const changeData = () => {
    const formData = new FormData();

    setLoading(true);

    formData.append('userId', formInfo.id);
    formData.append('lastname', formInfo.lastname ?? '');
    formData.append('firstname', formInfo.firstname ?? '');
    formData.append('surname', formInfo.surname ?? '');
    formData.append('gender', formInfo.gender ?? '');
    formData.append('date_of_birth', formInfo.date_of_birth ? normalizeDate(formInfo.date_of_birth) : '');
    formData.append('place_of_birth', formInfo.place_of_birth ?? '');
    formData.append('registration_address', formInfo.registration_address ?? '');
    formData.append('postal_address', formInfo.postal_address ?? '');
    formData.append('isSameAddress', isSameAddress);
    formData.append('TIN', formInfo.TIN ?? '');
    formData.append('fileNameTIN', files.TIN?.name || '');
    formData.append('SNILS', formInfo.SNILS ?? '');
    formData.append('fileNameSNILS', files.SNILS?.name || '');
    formData.append('registry_am_number', formInfo.registry_am_number ?? '');
    formData.append('email', formInfo.email ?? '');
    formData.append('additional_email', formInfo.additional_email ?? '');
    formData.append('phone_number', formInfo.phone_number ?? '');
    formData.append('additional_phone_number', formInfo.additional_phone_number ?? '');
    // Добавляем файлы, если они есть
    if (files.userAvatar) {
      formData.append('file_userAvatar', files.userAvatar);
    }
    if (files.passport) {
      formData.append('file_passport', files.passport);
    }
    if (files.TIN) {
      formData.append('file_TIN', files.TIN);
    }
    if (files.SNILS) {
      formData.append('file_SNILS', files.SNILS);
    }
    if (files.personalDataConsent) {
      formData.append('file_personalDataConsent', files.personalDataConsent);
    }

    axios.put('/api/change-main-user-data', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(() => {
      setStatus(<a>Данные сохранены</a>);
      openTimeModal('Данные успешно сохранены', 'success');
      setReload(true);
      setFiles({
        passport: null,
        TIN: null,
        SNILS: null,
      });
      setIsFormChanged(false);
    })
    .catch((error) => {
      console.error(error);
      setStatus(<a className='mainLabelError'>{`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`}</a>);
    })
    .finally(() => {
      setLoading(false);
    });
  };

  const handleSave = () => {
    if (validateForm()) {
      changeData();
    }
  };


  const [files, setFiles] = React.useState({
    userPicture: null,
    userAvatar: null,
    passport: null,
    TIN: null,
    SNILS: null,
    consent: null,
  });
  const handleFileChange = (field) => (e) => {
    const file = e.target.files[0];
    setFiles((prev) => ({
      ...prev,
      [field]: file || null,
    }));
    setIsFormChanged(true);
  };

  const handleFileReset = (field) => () => {
    setFiles((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const handleDeleteFile = (id) => {
      const token = localStorage.getItem('token');

      setLoading(true);
  
      axios.delete(`/api/delete-user-file/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      .then(() => {
        setReload(true);
        openTimeModal('Файл успешно удален', 'success');
      })
      .catch((error) => {
        openTimeModal(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
      });
  };

  const changeRole = (newRole, userId) => {
    const token = localStorage.getItem('token');

    axios.put('/api/change-role', {}, {
        headers: { 
            Authorization: `Bearer ${token}`,
        },
        params: {
            role: newRole,
            userId: userId,
        },
    })
    .then(() => {
        openTimeModal('Тип учётной записи успешно изменён', 'success');
    })
    .catch((error) => {
        console.error(error);
        openTimeModal(`Не удалось изменить тип учётной записи. ${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
    })
    .finally(() => {
        window.location.reload();
    });
  };

  const changeAccess = (userIdForChange) => {
    const token = localStorage.getItem('token');

    setLoading(true);

    axios.put(`/api/change-access?userId=${userIdForChange}`, {}, {
        headers: {
            Authorization: `Bearer ${token}`,
        }
    })
    .then((response) => {
        setReload(true);
        if (response.data == 'На рассмотрении') {
            openTimeModal('Доступ отозван', 'success');
        } else {
            openTimeModal('Доступ разрешён', 'success');
        }
    })
    .catch((error) => {
        console.error(error);
        openTimeModal(`Не удалось изменить параметр доступа к системе. ${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
    })
  };

  const changeApproval = (userId) => {
    const token = localStorage.getItem('token');

    setLoading(true);

    axios.put(`/api/block-unblock-acc?userId=${userId}`, {}, {
        headers: {
            Authorization: `Bearer ${token}`,
        }
    })
    .then(() => {
        openTimeModal('Тип учётной записи успешно изменён', 'success');
    })
    .catch((error) => {
        console.error(error);
        openTimeModal(`Не удалось изменить тип учётной записи. ${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
    })
    .finally(() => {
        setReload(true);
        setLoading(false);
    });
  };
  

  return (
    <div className='pageLimiter'>
      {(!formInfo.email && loading) ? <Loading />
      : warning ? <WarningPanel text={warning} />
      :
      <>
        {formInfo.approval=='Отключен' && <WarningPanel text={'Данная учётная запись деактивирована, арбитражный управляющий не сможет зайти в систему'} type={'error'} />}
        {formInfo.approval=='На рассмотрении' && <WarningPanel text={'Доступ к системе данной учётной записи на рассмотрении'} />}
        <div className='labelInputWrapper'>
          <a className='label'>Фото</a>
          <div className='labelInputRight'>
            <p className='description'>Временно недоступно</p>
            {/* {loading ? <Loading type='inline' /> : <AvatarEditor files={files} setFiles={setFiles} handleFileChange={handleFileChange} handleFileReset={handleFileReset} />}
            <FilesMiniTable 
              file_ids={formInfo.files.userAvatar || []}
              userId={formInfo.id}
              isAdmin={isAdmin}
              reload={reload}
              handleDelete={handleDeleteFile}
              onSuccess={() => {setReload(true); setIsFormChanged(false)}} 
            /> */}
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Фамилия</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input 
                type='text' 
                value={formInfo.lastname} 
                onChange={handleChange('lastname')} 
                className={errorsForm.lastname ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
                required />
              {errorsForm.lastname && <div className='errorUnderInput'>{errorsForm.lastname}</div>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Имя</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input 
                type='text' 
                value={formInfo.firstname} 
                onChange={handleChange('firstname')} 
                className={errorsForm.firstname ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
                required />
              {errorsForm.firstname && <div className='errorUnderInput'>{errorsForm.firstname}</div>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Отчество</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input 
                type='text' 
                value={formInfo.surname} 
                onChange={handleChange('surname')} 
                className={errorsForm.surname ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
                required />
              {errorsForm.surname && <div className='errorUnderInput'>{errorsForm.surname}</div>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Пол</a>
          <div className='labelInputRight'>
            <div className='radioSwitchWrapper'>
              <label className={`radioSwitchLabel ${formInfo.gender === 'Муж' ? 'selected' : ''}`}>
                <input
                    type='radio'
                    name='typeSelector'
                    value='Муж'
                    checked={formInfo.gender === 'Муж'}
                    onChange={handleChange('gender')}
                />
                <span>Мужской</span>
              </label>
              <label className={`radioSwitchLabel ${formInfo.gender === 'Жен' ? 'selected' : ''}`}>
                <input
                    type='radio'
                    name='typeSelector'
                    value='Жен'
                    checked={formInfo.gender === 'Жен'}
                    onChange={handleChange('gender')}
                />
                <span>Женский</span>
              </label>
            </div>
          </div>
        </div>

        <div style={{height: '20px'}}></div>

        <div className='labelInputWrapper'>
          <a className='label'>Дата рождения</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input type='date' 
                value={formInfo.date_of_birth?.split('T')[0]}
                onChange={handleChange('date_of_birth')} 
                className={errorsForm.date_of_birth ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
                required />
              {errorsForm.date_of_birth && <div className='errorUnderInput'>{errorsForm.date_of_birth}</div>}
            </div>
          </div>
        </div>

        <div className='labelInputWrapper'>
          <a className='label'>Место рождения</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input type='text' 
                value={formInfo.place_of_birth} 
                onChange={handleChange('place_of_birth')} 
                className={errorsForm.place_of_birth ? 'inputError' : ''}
                placeholder='Например, ГОРОД МОСКВА' 
                minLength='' 
                maxLength='100' 
                required />
              {errorsForm.place_of_birth && <div className='errorUnderInput'>{errorsForm.place_of_birth}</div>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Адрес регистрации</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input type='text' 
                value={formInfo.registration_address} 
                onChange={handleChange('registration_address')} 
                className={errorsForm.registration_address ? 'inputError' : ''}
                placeholder='Например, Россия, г.Санкт-Петербург, ул.Рубинштейна, 8' 
                minLength='' 
                maxLength='150' 
                disabled={(!isAdmin && formInfo.registration_address)}
                required 
              />
              {errorsForm.registration_address && <div className='errorUnderInput'>{errorsForm.registration_address}</div>}
              {(!isAdmin && formInfo.registration_address) && <p className='description'>Для смены адреса регистрации необходимо отправить в СРО заявление о смене данных в Реестре арбитражных управляющих</p>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Почтовый адрес</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input type='text' 
                value={formInfo.postal_address} 
                onChange={handleChange('postal_address')} 
                className={errorsForm.postal_address ? 'inputError' : ''}
                placeholder='Например, Россия, г.Санкт-Петербург, ул.Рубинштейна, 8' 
                minLength='' 
                maxLength='150' 
                disabled={(!isAdmin && formInfo.registration_address) || isSameAddress} 
              />
              {errorsForm.postal_address && <div className='errorUnderInput'>{errorsForm.postal_address}</div>}
            </div>
            <label className='legCheckboxWrapper'>
              <input 
                type='checkbox'
                checked={isSameAddress}
                onChange={handleSameAddressToggle} />
              <a>Совпадает с адресом регистрации</a>
            </label>
            {(!isAdmin && formInfo.postal_address) && <p className='description'>Для смены почтового адреса необходимо отправить в СРО заявление о смене данных в Реестре арбитражных управляющих</p>}
          </div>
        </div>

        <div className='labelInputWrapper'>
          <a className='label'>Паспорт</a>
          <div className='labelInputRight'>
            {/* <input type='text' 
              value={formInfo.place_of_birth} 
              onChange={handleChange('place_of_birth')} 
              className={errorsForm.place_of_birth ? 'inputError' : ''}
              placeholder='Cерия и номер' 
              minLength='' 
              maxLength='100' 
              required 
            /> */}
            {loading ? <LoadingBlock width="100%" height="30px" /> 
            : <FileUploadField
              label='Прикрепить паспорт'
              file={files.passport}
              onChange={handleFileChange('passport')}
              onReset={handleFileReset('passport')}
            />}
            <FilesMiniTable 
              file_ids={formInfo.files.passport || []}
              isAdmin={isAdmin}
              reload={reload}
              handleDelete={handleDeleteFile}
              onSuccess={() => {setReload(true); setIsFormChanged(false)}} 
            />
            {errorsForm.passport && <div className='errorUnderInput'>{errorsForm.passport}</div>}
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>ИНН</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <InputMask
                mask="999999999999"
                value={formInfo.TIN}
                onChange={handleChange('TIN')}
                maskChar={null}
              >
                {(inputProps) => (
                  <input
                    {...inputProps}
                    type='text'
                    placeholder=''
                    className={errorsForm.TIN ? 'inputError' : ''}
                  />
                )}
              </InputMask>
              {errorsForm.TIN && <div className='errorUnderInput'>{errorsForm.TIN}</div>}
            </div>
            {loading ? <LoadingBlock width="100%" height="30px" /> 
            : <FileUploadField
              label='Прикрепить ИНН'
              file={files.TIN}
              onChange={handleFileChange('TIN')}
              onReset={handleFileReset('TIN')}
            />}
            <FilesMiniTable 
              file_ids={formInfo.files.TIN || []}
              userId={formInfo.id}
              isAdmin={isAdmin}
              reload={reload}
              handleDelete={handleDeleteFile}
              onSuccess={() => {setReload(true); setIsFormChanged(false)}} 
            />
            {errorsForm.FileTIN && <div className='errorUnderInput'>{errorsForm.FileTIN}</div>}
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>СНИЛС</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <InputMask
                mask="999-999-999 99"
                value={formInfo.SNILS}
                onChange={handleChange('SNILS')}
              >
                {(inputProps) => (
                  <input
                    {...inputProps}
                    type='text'
                    placeholder=''
                    className={errorsForm.SNILS ? 'inputError' : ''}
                  />
                )}
              </InputMask>
              {errorsForm.SNILS && <div className='errorUnderInput'>{errorsForm.SNILS}</div>}
            </div>
            {loading ? <LoadingBlock width="100%" height="30px" /> 
            : <FileUploadField
              label='Прикрепить СНИЛС'
              file={files.SNILS}
              onChange={handleFileChange('SNILS')}
              onReset={handleFileReset('SNILS')}
            />}
            <FilesMiniTable 
              file_ids={formInfo.files.SNILS || []}
              userId={formInfo.id}
              isAdmin={isAdmin}
              reload={reload}
              handleDelete={handleDeleteFile}
              onSuccess={() => {setReload(true); setIsFormChanged(false)}} 
            />
            {errorsForm.FileSNILS && <div className='errorUnderInput'>{errorsForm.FileSNILS}</div>}
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Номер в СГРАУ</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input 
                type='text' 
                value={formInfo.registry_am_number} 
                onChange={handleChange('registry_am_number')} 
                className={errorsForm.registry_am_number ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
                required />
              {errorsForm.registry_am_number && <div className='errorUnderInput'>{errorsForm.registry_am_number}</div>}
            </div>
          </div>
        </div>

        <div style={{height: '20px'}}></div>

        <div className='labelInputWrapper'>
          <a className='label'>Адрес электронной почты</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input 
                type='email' 
                value={formInfo.email} 
                onChange={handleChange('email')} 
                className={errorsForm.email ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
                disabled={(!isAdmin && formInfo.email)}
                required 
              />
              {errorsForm.email && <div className='errorUnderInput'>{errorsForm.email}</div>}
              {(!isAdmin && formInfo.email) && <p className='description'>Для смены адреса электронной почты необходимо отправить в СРО заявление о смене данных в Реестре арбитражных управляющих</p>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Дополнительный адрес электронной почты</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <input 
                type='email' 
                value={formInfo.additional_email} 
                onChange={handleChange('additional_email')} 
                className={errorsForm.additional_email ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
              />
              {errorsForm.additional_email && <div className='errorUnderInput'>{errorsForm.additional_email}</div>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Номер телефона</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              {(!isAdmin && formInfo.phone_number) ?
              <input 
                type='email' 
                value={formInfo.phone_number} 
                className={errorsForm.phone_number ? 'inputError' : ''}
                placeholder='' 
                minLength='' 
                maxLength='' 
                disabled
              />
              :
              <InputMask
                mask="+7 (999) 999-99-99"
                value={formInfo.phone_number}
                onChange={handleChange('phone_number')}
                required
              >
                {(inputProps) => 
                <input 
                  type='text' 
                  className={errorsForm.phone_number ? 'inputError' : ''}
                  {...inputProps} 
                />}
              </InputMask>}
              {errorsForm.phone_number && <div className='errorUnderInput'>{errorsForm.phone_number}</div>}
              {(!isAdmin && formInfo.phone_number) && <p className='description'>Для смены номера телефона необходимо отправить в СРО заявление о смене данных в Реестре арбитражных управляющих</p>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Дополнительный номер телефона</a>
          <div className='labelInputRight'>
            <div className='inputWrapper'>
              <InputMask
                mask="+7 (999) 999-99-99"
                value={formInfo.additional_phone_number}
                onChange={handleChange('additional_phone_number')}
                required
              >
                {(inputProps) => 
                <input 
                  type='text' 
                  className={errorsForm.additional_phone_number ? 'inputError' : ''}
                  {...inputProps} />}
              </InputMask>
              {errorsForm.additional_phone_number && <div className='errorUnderInput'>{errorsForm.additional_phone_number}</div>}
            </div>
          </div>
        </div>
        <div className='labelInputWrapper'>
          <a className='label'>Согласие на обработку персональных данных</a>
          <div className='labelInputRight'>
            {templateConsentFileId && 
            <div className='horizontalContainer'>
                <a>Скачать шаблон для самостоятельного заполнения</a>
                <ViewFileById fileId={templateConsentFileId} />
            </div>}
            {loading ? <LoadingBlock width="100%" height="30px" /> 
            : <FileUploadField
              label='Прикрепить согласие на обработку персональных данных'
              file={files.personalDataConsent}
              onChange={handleFileChange('personalDataConsent')}
              onReset={handleFileReset('personalDataConsent')}
            />}
            <FilesMiniTable 
              file_ids={formInfo.files.personalDataConsent || []}
              userId={formInfo.id}
              isShowNullTable={true}
              isAdmin={isAdmin}
              deleteBan={true}
              handleDelete={handleDeleteFile}
              onSuccess={() => {setReload(true); setIsFormChanged(false)}} 
            />
          </div>
        </div>

        <div className='labelInputWrapper'>
          <a className='label'>Подпись</a>
          <div className='labelInputRight'>
              <button 
                className='button miniButton' 
                onClick={() => {
                  navigate(`/safety/signature/${formInfo.id}`, { state: { from: location.pathname + location.search + location.hash }})
                }}
              >
                Перейти
              </button>
          </div>
        </div>

        {isAdmin &&
        <div className='labelInputWrapper'>
          <a className='label'>Опасная область</a>
          <div className='labelInputRight'>
            <span style={{display: 'flex', flexDirection: 'column', gap: '10px', border: '1px #ff818266 solid', padding: '5px', borderRadius: '6px', boxSizing: 'border-box'}}>
              <p className='description'>Здесь можно изменить тип учётной записи, а также отозвать доступ к системе. Используйте с осторожностью</p>
              {(importantAccountData.id !== formInfo.id) ?
              <>
                {formInfo.access_level !== 'Администратор' ?
                <>
                <div>
                    <select 
                        key={formInfo.role}
                        onChange={(e) => changeRole(e.target.value, formInfo.id)} 
                        defaultValue={formInfo.role}
                    >
                        <option value="" disabled>Изменить тип аккаунта</option>
                        <option value="Арбитражный управляющий">Арбитражный управляющий</option>
                        <option value="АппаратСРО">АппаратСРО</option>
                    </select>
                    <p className='inTableRowDescription'>Нажмите и выберите нужный тип учетной записи для изменения. Изменения вступят в силу сразу после выбора. Обратите внимание, что страница будет перезагружена</p>
                </div>

                {loading ? <LoadingBlock width="100%" height="25px" /> 
                : formInfo.approval !== 'Отключен' ?
                <button 
                  className='tableButton' 
                  style={{width: '100%'}} 
                  onClick={() => changeAccess(formInfo.id)}
                  disabled={loading}
                >
                  {formInfo.approval === 'На рассмотрении' ? 'Выдать доступ к системе' : 'Отозвать доступ к системе'}
                </button>
                : 
                null}
                {loading ? <LoadingBlock width="100%" height="25px" /> 
                :
                <button 
                  className='tableButton red' 
                  style={{width: '100%'}} 
                  onClick={() => changeApproval(formInfo.id)}
                  disabled={loading}
                >
                  {formInfo.approval === 'Отключен' ? 'Активировать учётную запись' : 'Деактивировать учётную запись'}
                </button>
                }
                </>
                :
                <p style={{color: 'red', fontSize: '14px'}}>У данного аккаунта тип учётной записи "Администратор". Изменение типа аккаунта и отзыв доступа к системе для администратора невозможно</p>}
              </> 
              : 
              <p style={{color: 'red', fontSize: '14px'}}>Вы просматриваете свой аккаунт. Изменение типа аккаунта и отзыв доступа к системе для самого себя невозможно</p>}
            </span>
          </div>
        </div>}

        {isAdmin &&
        <div className='labelInputWrapper'>
          <a className='label'>Дополнительная информация</a>
          <div className='labelInputRight'>
            <a style={{fontStyle: 'italic'}}>Дата и время регистрации:</a>
            <a>{formatDateTime(formInfo.created_at)}</a>
          </div>
        </div>}

        <div style={{height: '50px'}}></div>

        {isFormChanged &&
          <div className='panelFormConfirmation'>
            <button type='button' className='button' onClick={handleSave}>Сохранить</button>
            <div>{status}</div>
          </div>
        }
      </>
      }
    </div>
  )
}

export default MainInfo;